import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, X, Sparkles, BookHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import client, { mediaUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";

// Pages from two finished Tales with Terry books, shipped with the app. The
// shop window is the first thing a visitor sees, so it must never be empty —
// these stand in until the backend has samples of its own.
//
// Two books rather than four pages of one, and deliberately interleaved: the
// question a visitor is actually asking is "what does MY child look like in
// this", so the pair that answers it is the same boy in two different art
// styles, seen next to each other.
const DEMO_SAMPLES = [
    { id: "demo-space-cover", image_url: "/demo/noah-space-cover.jpg",
      caption: "Noah's Space Voyage — cover, Pixar style" },
    { id: "demo-forest-cover", image_url: "/demo/noah-forest-cover.jpg",
      caption: "Noah's Whispering Forest — cover, claymation" },
    { id: "demo-space-rocket", image_url: "/demo/noah-space-rocket.jpg",
      caption: "Page 2 — Noah and Byte climb aboard" },
    { id: "demo-forest-path", image_url: "/demo/noah-forest-path.jpg",
      caption: "Page 2 — Noah and Moss follow the whispers" },
];

// Public — no sign-in. This is the shop window: a visitor sees real pages
// before they are asked for an account or a rupee.
export default function Samples() {
    const [samples, setSamples] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        client.get("/samples")
            .then(({ data }) => setSamples(data.samples?.length ? data.samples : DEMO_SAMPLES))
            .catch(() => setSamples(DEMO_SAMPLES))
            .finally(() => setLoading(false));
    }, []);

    const move = useCallback((delta) => {
        setLightbox((cur) => {
            if (!cur || samples.length === 0) return cur;
            const i = samples.findIndex((s) => s.id === cur.id);
            return samples[(i + delta + samples.length) % samples.length] || cur;
        });
    }, [samples]);

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
            if (e.key === "ArrowRight") move(1);
            if (e.key === "ArrowLeft") move(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, move]);

    return (
        <div className="min-h-screen magic-bg">
            <Navbar />

            <section className="max-w-6xl mx-auto px-6 pt-14 pb-8 text-center">
                <div className="text-xs tracking-[0.28em] uppercase text-plum/60 mb-3">Real Pages</div>
                <h1 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl mx-auto">
                    This is what lands in your library.
                </h1>
                <div className="script-note text-3xl mt-2">real pages, from real books</div>
                <p className="text-plum/60 mt-4 max-w-xl mx-auto">
                    Every page is illustrated by hand from the photos you send — the same faces,
                    page after page. Have a look before you decide.
                </p>
            </section>

            <div className="max-w-6xl mx-auto px-6 pb-16">
                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="samples-skeleton">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="card-cloud aspect-square animate-pulse bg-lavender/40" />
                        ))}
                    </div>
                ) : samples.length === 0 ? (
                    <div className="card-cloud p-12 text-center text-plum/60 flex flex-col items-center gap-4"
                        data-testid="samples-empty">
                        <BookHeart className="w-8 h-8 text-bubblegum" />
                        <div className="max-w-sm">
                            Sample pages are on their way. In the meantime, you can start your own story
                            and see it come together.
                        </div>
                        <Link to="/auth?mode=signup">
                            <button className="btn-sticker text-sm">✨ Start the magic</button>
                        </Link>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="samples-grid">
                        {samples.map((s, i) => (
                            <motion.button
                                key={s.id}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: (i % 6) * 0.06 }}
                                whileHover={{ y: -4 }}
                                onClick={() => setLightbox(s)}
                                className="card-cloud overflow-hidden text-left group"
                                data-testid={`sample-${i}`}
                            >
                                {/* No <img>: a CSS background has no "Save image as", no drag
                                    to desktop, and no long-press save sheet on mobile. None of
                                    this stops a screenshot — nothing on the web can — which is
                                    why every sample is watermarked in the pixels before upload. */}
                                <div className="aspect-square overflow-hidden bg-lavender/40 select-none"
                                    role="img" aria-label={s.caption || `Sample page ${i + 1}`}
                                    onContextMenu={(e) => e.preventDefault()}
                                    style={{
                                        backgroundImage: `url(${mediaUrl(s.image_url)})`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        WebkitTouchCallout: "none",
                                    }} />
                                {s.caption && (
                                    <div className="p-4 text-sm text-plum/70">{s.caption}</div>
                                )}
                            </motion.button>
                        ))}
                    </div>
                )}

                <div className="card-cloud p-8 md:p-10 mt-14 text-center">
                    <h2 className="font-display text-3xl tracking-tight">Ready to make one for your family?</h2>
                    <p className="text-plum/60 mt-3 max-w-lg mx-auto">
                        Pick your people, choose a world, and we'll illustrate the whole book.
                        Digital storybook <strong className="text-plum">₹399</strong> · printed hardcover{" "}
                        <strong className="text-plum">₹799</strong>.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center mt-6">
                        <Link to="/auth?mode=signup">
                            <Button size="lg" className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                data-testid="samples-cta-btn">
                                <Sparkles className="w-4 h-4 mr-1" /> Create your story
                            </Button>
                        </Link>
                        <Link to="/">
                            <Button size="lg" variant="ghost" className="rounded-full">Back to home</Button>
                        </Link>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setLightbox(null)}
                        className="fixed inset-0 z-50 bg-plum/80 backdrop-blur-sm grid place-items-center p-4"
                        data-testid="sample-lightbox"
                    >
                        <button onClick={() => setLightbox(null)} aria-label="Close"
                            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/90 grid place-items-center">
                            <X className="w-5 h-5 text-plum" />
                        </button>
                        {samples.length > 1 && (
                            <>
                                <button aria-label="Previous"
                                    onClick={(e) => { e.stopPropagation(); move(-1); }}
                                    className="absolute left-3 md:left-8 w-11 h-11 rounded-full bg-white/90 grid place-items-center">
                                    <ArrowLeft className="w-5 h-5 text-plum" />
                                </button>
                                <button aria-label="Next"
                                    onClick={(e) => { e.stopPropagation(); move(1); }}
                                    className="absolute right-3 md:right-8 w-11 h-11 rounded-full bg-white/90 grid place-items-center">
                                    <ArrowRight className="w-5 h-5 text-plum" />
                                </button>
                            </>
                        )}
                        <motion.div
                            key={lightbox.id}
                            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                            onClick={(e) => e.stopPropagation()}
                            onContextMenu={(e) => e.preventDefault()}
                            role="img" aria-label={lightbox.caption || "Sample page"}
                            className="h-[85vh] w-[92vw] rounded-2xl shadow-2xl select-none"
                            style={{
                                backgroundImage: `url(${mediaUrl(lightbox.image_url)})`,
                                backgroundSize: "contain",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                                WebkitTouchCallout: "none",
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
